/**
 * The /apply resume link's one shape: path plus a `token` query param. Owned
 * here so the emailed link (server) and the post-signup redirect (browser)
 * can never drift — the wizard reads and strips the same param name.
 *
 * These two exports are the browser-safe part of this module: pure, no env
 * access. The builders below them are not — see their notes.
 */
export const APPLY_TOKEN_PARAM = 'token'

export function applyResumePath(token: string): string {
  return `/apply?${APPLY_TOKEN_PARAM}=${encodeURIComponent(token)}`
}

/**
 * Builds the /apply deep link carried in prospect emails (LG-118).
 *
 * Shared by the wizard router (abandonment emails) and the admin
 * propose-timeslots action (the "pick a time" link in the outreach email) so
 * the two can never drift into different link shapes.
 *
 * No base configured → emit no link at all. A relative fallback would be
 * persisted as a CIO trait and ship as a dead href.
 *
 * Server-only: reads `process.env`, which the browser bundle does not define
 * (only NODE_ENV is), so calling this from a component throws at runtime
 * while passing typecheck and jsdom tests. Components use applyResumePath.
 */
export function buildResumeUrl(token: string): string {
  const base = process.env.AUDITION_PUBLIC_BASE_URL
  if (!base) return ''
  return `${base}${applyResumePath(token)}`
}

/**
 * The bare /apply link, for someone who has no application to resume.
 *
 * Tokenless on purpose: {@link buildResumeUrl} deep-links a prospect back
 * into the wizard they already started, and the one caller here is the
 * opposite case — a cal.com booking from an address no application claims,
 * declined with "apply first". Minting a token would mean inventing the
 * submission the message exists to say does not exist.
 *
 * Same empty-string-when-unconfigured contract as its neighbour.
 *
 * Server-only for the same reason as {@link buildResumeUrl}.
 */
export function buildApplyUrl(): string {
  const base = process.env.AUDITION_PUBLIC_BASE_URL
  if (!base) return ''
  return `${base}/apply`
}
